import React, { useEffect, useState } from "react";
import { RiCloseFill, RiDeleteBinLine } from 'react-icons/ri';
import TagInputs from "../layouts/TagInputs";
import SuccessModal from "../modals/SuccessModal";
import UploadButton from "../../Input/UploadButton";
import { get, post, put } from "../../utility/fetch";
import notification from "../../utility/notification";
import TemplateSelector from "../layouts/TemplateSelector";
import DynamicForm from "../layouts/DynamicForm";
import { calculateAge, formatTextareaContent } from "../../utility/general";
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";

function ProcedureEditsModal({ patientInfo, review, noDoctor, closeModal, getProcedures, readOnly, walkin }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documentArray, setDocumentArray] = useState([]);
    const [labResults, setLabResults] = useState({});
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labServices, setLabServices] = useState([]);
    const [aiFeedback, setAiFeedback] = useState('');
    const [vitalId, setVitalId] = useState(null);

    // Approver selection
    const [employees, setEmployees] = useState([]);
    const [selectedApprover, setSelectedApprover] = useState(null);
    const [selectedApproverId, setSelectedApproverId] = useState(null);

    const [vitals, setVitals] = useState({
        dateOfVisit: '',
        temperature: '',
        bloodPressure: '',
        oxygenSaturation: '',
        bloodSugar: '',
        heartPulse: '',
        respiratory: '',
        height: '',
        weight: '',
        bmi: '',
        vitalNurseEmployeeId: 0,
        patientId: patientInfo?.patient?.id || 0,
        appointmentId: patientInfo?.appointmentId || 0,
        labRequestId: patientInfo?.id
    });

    const [payload, setPayload] = useState({
        testReports: [],
        vitalId: 0
    });

    const [initialFormValues, setInitialFormValues] = useState({});

    const isMobile = window.innerWidth < 768;

    console.log(patientInfo)

    const getVitalId = async () => {
        let res = await get(`/vital/list/appointment/${patientInfo?.appointmentId}`);
        setVitalId(res?.data[0]?.id);
    };

    const getEmployees = async () => {
        try {
            const res = await get(`/employee/list/false`);
            console.log(res)
            setEmployees(res?.data?.recordList || []);
        } catch (err) {
            console.error("Failed to fetch employees", err);
        }
    }

    const handleVitalChange = (field) => (e) => {
        if (field === "height" || field === "weight" || field === "heartPulse" || field === "temperature") {
            setVitals(prevVitals => ({
                ...prevVitals,
                [field]: Number(e.target.value),
            }));
        }
        else {
            setVitals(prevVitals => ({
                ...prevVitals,
                [field]: e.target.value,
            }));
        }
    };

    useEffect(() => {
        getEmployees();
        if (vitals.height && vitals.weight) {
            const heightInMeters = parseFloat(vitals.height) / 100;
            const weightInKg = parseFloat(vitals.weight);

            if (heightInMeters > 0 && weightInKg > 0) {
                const calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
                setVitals(prevVitals => ({
                    ...prevVitals,
                    bmi: parseFloat(calculatedBmi),
                }));
            } else {
                setVitals(prevVitals => ({ ...prevVitals, bmi: '' }));
            }
        } else {
            setVitals(prevVitals => ({ ...prevVitals, bmi: '' }));
        }
    }, [vitals.height, vitals.weight]);

    useEffect(() => {
        setVitals(prevVitals => ({
            ...prevVitals,
            patientId: patientInfo?.patient?.id || prevVitals.patientId,
            appointmentId: patientInfo?.appointmentId || prevVitals.appointmentId,
        }));
    }, [patientInfo]);

    useEffect(() => {
        if (!patientInfo) return;

        const sources = walkin ? (patientInfo?.walkInTestRequests || []) : (patientInfo?.patientTestReports || []);
        const templateIds = Array.from(new Set(sources.map(s => s?.internalLabService?.id).filter(Boolean)));
        setSelectedTemplates(templateIds);

        const defaults = {};
        const reports = patientInfo?.patientTestReports || [];
        reports.forEach(pr => {
            (pr.labTestServiceResults || []).forEach(r => {
                const fieldId = r?.testDetails?.id ?? r?.labServiceDetailsId ?? r?.labServiceDetailsId;
                if (fieldId) defaults[fieldId] = r?.result ?? '';
            });
        });

        (patientInfo?.walkInTestRequests || []).forEach(wr => {
            (wr?.labTestServiceResults || []).forEach(r => {
                const fieldId = r?.testDetails?.id ?? r?.labServiceDetailsId;
                if (fieldId) defaults[fieldId] = r?.result ?? '';
            });
        });

        setInitialFormValues(defaults);

    }, [patientInfo, walkin]);

    const getInternalLabServices = async () => {
        setLoading(true);
        try {
            let labServiceIds = [];
            if (walkin) {
                labServiceIds = (patientInfo?.walkInTestRequests || [])
                    .filter(item => item != null)
                    .map(r => r.internalLabService?.id)
                    .filter(Boolean);
            } else {
                labServiceIds = (patientInfo?.patientTestReports || [])
                    .filter(item => item != null)
                    .map(r => r.internalLabService?.id)
                    .filter(Boolean);
            }

            const res = await get(`/internallabservice/list/${1}/${1000}`);
            const allServices = res?.data?.recordList || [];
            setLabServices(
                allServices
                    .filter(item => labServiceIds.length === 0 ? true : labServiceIds.includes(item?.id))
                    .map(item => ({
                        id: item?.id,
                        name: item?.name,
                        fields: (item?.labServiceTestDetails || []).map(field => ({
                            name: field?.parameter,
                            id: field?.id
                        }))
                    }))
            );
            setLoading(false);
        } catch (error) {
            console.error('Error fetching lab services:', error);
            setLoading(false);
        }
    };

    const handleSave = async (formData) => {
        let vitalData;

        vitalData = await submitVitals();

        let vitalId = 0;

        if (vitalData) {
            vitalId = vitalData.id;
        }

        try {
            
            const testReports = selectedTemplates.map(templateId => {
                const template = labServices.find(t => t.id === templateId);
                const fields = template?.fields || [];

                const matchingRequest = (!walkin ? (patientInfo?.patientTestReports || []).find(tr => tr.internalLabService?.id === templateId)
                    : (patientInfo?.patientTestReports || []).find(tr => tr.internalLabService?.id === templateId)
                ) || (patientInfo?.walkInTestRequests || []).find(tr => tr.internalLabService?.id === templateId) || {};

                const testReportId = matchingRequest?.id || 0;

                return !walkin ? {
                    testReportId,
                    internalLabServiceId: templateId,
                    userToApproveId:selectedApproverId,
                    labTestServiceResults: fields.map(field => ({
                        labServiceTestResultId: 0,
                        labServiceDetailsId: field.id,
                        result: formData[field.id] ?? initialFormValues[field.id] ?? ''
                    })),
                    comment: aiFeedback,
                    patientLabDocuments: documentArray.map(doc => ({ docName: doc.name, docPath: doc.path }))
                } : {
                    walkinTestRequestId: testReportId,
                    userToApproveId:selectedApproverId,
                    internalLabServiceId: templateId,
                    labTestServiceResults: fields.map(field => ({
                        labServiceTestResultId: 0,
                        labServiceDetailsId: field.id,
                        result: formData[field.id] ?? initialFormValues[field.id] ?? ''
                    })),
                    comment: aiFeedback,
                    patientLabDocuments: documentArray.map(doc => ({ docName: doc.name, docPath: doc.path }))
                }
            });

            let res;
            const updatePayload = { testReports };

            if (review === true) {
                // review mode -> always update existing report (use walkin vs referral endpoint)

                if (walkin) {
                    // walk-in update endpoint
                    const patientLabReportId = patientInfo?.id;
                    res = await put(`/walkinpatientlabreport/lab-report/${patientLabReportId}/update-walk-in-patient-test-report`, updatePayload);
                } else {
                    // referral update endpoint
                    const patientLabReportId = patientInfo?.id;
                    res = await put(`/patientlabreport/lab-report/${patientLabReportId}/update-patient-test-report`, updatePayload);
                }
            } else {
                if (walkin) {
                    // walk-in update endpoint
                    const patientLabReportId = patientInfo?.id;
                    res = await post(`/walkinpatientlabreport/walk-in-lab-report`, { walkInLabRequestId: patientLabReportId, ...updatePayload });
                } else {
                    // referral update endpoint
                    const patientLabReportId = patientInfo?.id;
                    res = await post(`/patientlabreport/internal-lab-report`, { labRequestId: patientLabReportId, vitalId: vitalId, ...updatePayload });
                }
            }

            if (res) {
                notification({ message: "Lab Report Successfully Updated", type: "success" });
                getProcedures();
                closeModal();
            }
        } catch (error) {
            console.error("Error submitting lab report:", error);
            notification({ message: "Failed to submit lab report", type: "error" });
        }
    };

    const submitVitals = async () => {
        try {
            let res = await post("/vital", vitals);

            if (res?.statusCode === 200) {
                notification({
                    type: "success",
                    message: res?.data?.message
                })

                return res?.data;
            }
            else {
                return;
            }
        } catch (error) {
            return;
        }
    }

    const analyzeFile = async (imageFile) => {
        if (!imageFile) {
            alert('Please provide a lab image for analysis.');
            return;
        }

        setLoading(true);
        setAiFeedback('');

        try {
            const formData = new FormData();
            formData.append('ImageToAnalyze', imageFile);
            formData.append('PatientId', 70);

            const res = await fetch('https://api.greenzonetechnologies.com.ng/ConnectedHealthWebApi/api/ImageAnalysis/analyze-xray', {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            setAiFeedback(data.data || '✅ AI feedback received.');
        } catch (err) {
            console.error(err);
            setAiFeedback('❌ Error: Failed to analyze image.');
        } finally {
            setLoading(false);
        }
    };

    const deleteDoc = (docName) => {
        setDocumentArray(prev => prev.filter(doc => doc.name !== docName));
    };

    useEffect(() => {
        if (readOnly) {
            getLabResults();
        }
        getInternalLabServices();
        if (walkin !== true) {
            getVitalId();
            setPayload((prev) => ({
                ...prev,
                labRequestId: patientInfo?.id || 0,
                vitalId: vitalId || 0
            }));
        }
        else {
            setPayload((prev) => ({
                ...prev,
                walkInLabRequestId: patientInfo?.id || 0
            }))
        }
    }, []);

    const getLabResults = async () => {
        try {
            let res = await get(`/patientinternallabreport/internal-lab-report/lab-request/${patientInfo?.id}`);
            setLabResults(res[0]);
        } catch (e) {
            console.error(e);
        }
    };

    const getFilteredWalkInTestRequests = () => {
        if (!walkin || !patientInfo?.walkInTestRequests || labServices.length === 0) {
            return patientInfo?.walkInTestRequests || [];
        }

        const labServiceIds = labServices.map(service => service.id);
        return patientInfo.walkInTestRequests.filter(request =>
            request?.internalLabService?.id && labServiceIds.includes(request.internalLabService.id)
        );
    };

    return (
        <div className="overlay">
            <RiCloseFill className='close-btn pointer' onClick={closeModal} />
            <div style={{ width: isMobile ? '95%' : '80%' }} className="modal-content h-80 w-80">
                <div className="w-100 m-t-20">
                    <div className="m-t-10">
                        <h2>Lab Work For {patientInfo?.walkinPatient ? `${patientInfo?.firstName || patientInfo?.patient?.firstName} ${patientInfo?.lastName || patientInfo?.patient?.lastName}` : `${patientInfo?.walkInPatient?.firstName + " " + patientInfo?.walkInPatient?.lastName}`}</h2>
                        <div className="tabs m-t-10 bold-text"></div>

                        {/* Patient Information Section */}
                        {
                            walkin &&
                            <div className="flex space-between m-l-40 p-20" style={isMobile ? { flexDirection: 'column', marginLeft: 0, padding: '10px' } : {}}>
                                <div className="flex flex-direction-v col-4" style={isMobile ? { width: '100%' } : {}}>
                                    <TagInputs label="Patient's Name" value={patientInfo?.walkInPatient?.firstName ? `${patientInfo?.walkInPatient?.firstName} ${patientInfo?.walkInPatient?.lastName}` : patientInfo?.patientFullName} readOnly />
                                    <TagInputs label="Gender" value={patientInfo?.walkInPatient?.gender || patientInfo?.gender} readOnly />
                                    <TagInputs label="Age" value={!patientInfo?.dateOfBirth ? `${calculateAge(patientInfo?.walkInPatient?.dateOfBirth)}` : patientInfo?.age} readOnly />
                                    {!noDoctor && <TagInputs label='Clinic' value={patientInfo?.initiatedHealthCareProvider?.name} readOnly />}
                                    {!noDoctor && <TagInputs label="Doctor's Name" value={`${patientInfo?.doctor?.firstName} ${patientInfo?.doctor?.lastName}`} readOnly />}
                                    {/* Approver selector - only show if not in review mode */}
                                    {!review && (
                                        <Box sx={{ mt: 1 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="approver-select-label">Approver</InputLabel>
                                                <Select
                                                    labelId="approver-select-label"
                                                    value={selectedApprover?.id ?? ''}
                                                    label="Approver"
                                                    onChange={(e) => {
                                                        const id = e.target.value;
                                                        console.log('Selected approver ID:', id);
                                                        const emp = employees.find(emp => emp.id === id) || null;
                                                        setSelectedApproverId(id);
                                                        setSelectedApprover(emp);
                                                    }}
                                                >
                                                    <MenuItem value="">— Select approver —</MenuItem>
                                                    {employees.map(emp => (
                                                        <MenuItem key={emp.id} value={emp.id}>
                                                            {emp.firstName} {emp.lastName}{emp?.role ? ` — ${emp.role}` : ''}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}
                                    <TagInputs label='Date Initiated' value={patientInfo?.createdAt ? new Date(patientInfo?.createdAt)?.toLocaleDateString() : new Date(patientInfo?.createdOn)?.toLocaleDateString()} readOnly />
                                    <TagInputs label='Time Initiated' value={patientInfo?.createdAt ? new Date(patientInfo?.createdAt)?.toLocaleTimeString() : new Date(patientInfo?.createdOn)?.toLocaleTimeString()} readOnly />
                                </div>
                                <div className="flex flex-direction-v col-8" style={isMobile ? { width: '100%', marginTop: '20px' } : {}}>
                                    {!noDoctor && <TagInputs label="Doctor's Diagnosis" type='textArea' value={patientInfo?.diagnosis} readOnly multiline />}
                                    <TagInputs label='Additional Notes' type='textArea' value={getFilteredWalkInTestRequests()[0]?.additionalNote || ''} readOnly multiline />
                                    <textarea className="h-30" rows={12} disabled value={formatTextareaContent(aiFeedback)} name="" id="" />
                                </div>
                            </div>
                        }

                        {
                            !walkin && <div className="flex space-between m-l-40 p-20" style={isMobile ? { flexDirection: 'column', marginLeft: 0, padding: '10px' } : {}}>
                                <div className="flex flex-direction-v col-4" style={isMobile ? { width: '100%' } : {}}>
                                    <TagInputs label="Patient's Name" value={`${patientInfo?.patient?.firstName} ${patientInfo?.patient?.lastName}`} readOnly />
                                    <TagInputs label=" Weight" value={`${patientInfo?.patient?.weight} Kg`} readOnly />
                                    <TagInputs label="Age" value={`${calculateAge(patientInfo?.patient?.dateOfBirth)}`} readOnly />
                                    {!noDoctor && <TagInputs label='Clinic' value={patientInfo?.initiatedHealthCareProvider?.name} readOnly />}
                                    {!noDoctor && <TagInputs label="Doctor's Name" value={`${patientInfo?.doctor?.firstName} ${patientInfo?.doctor?.lastName}`} readOnly />}
                                    <TagInputs label='Date Initiated' value={new Date(patientInfo?.createdAt)?.toLocaleDateString()} readOnly />
                                    <TagInputs label='Time Initiated' value={new Date(patientInfo?.createdAt)?.toLocaleTimeString()} readOnly />
                                </div>
                                <div className="flex flex-direction-v col-8" style={isMobile ? { width: '100%', marginTop: '20px' } : {}}>
                                    {!noDoctor && <TagInputs label="Doctor's Diagnosis" type='textArea' value={patientInfo?.diagnosis} readOnly multiline />}
                                    <TagInputs label='Additional Notes' type='textArea' value={Array.isArray(patientInfo.walkInTestRequests) && patientInfo?.walkInTestRequests[0]?.additionalNote} readOnly multiline />
                                    {aiFeedback && (
                                        <Box sx={{ p: 2, backgroundColor: '#f0fdf4', borderRadius: 2, borderColor: "green" }}>
                                            <Typography variant="subtitle1">AI Feedback:</Typography>
                                        </Box>
                                    )}
                                    <textarea rows={10} disabled value={formatTextareaContent(aiFeedback)} name="" id="" />
                                </div>
                            </div>
                        }

                        {/* Vitals Section - only show if not in review mode */}
                        {(!review && !walkin )&& <div className="vitals-section m-t-20" style={{ marginTop: '20px', padding: '15px', border: '1px solid #eee', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                            <h3 style={{ marginBottom: '15px', color: '#333' }}>Patient Vitals</h3>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Date of Visit"
                                        type="date"
                                        value={vitals.dateOfVisit}
                                        onChange={handleVitalChange('dateOfVisit')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Temperature (°C)"
                                        type="number"
                                        value={vitals.temperature}
                                        onChange={handleVitalChange('temperature')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Blood Pressure (mmHg)"
                                        value={vitals.bloodPressure}
                                        onChange={handleVitalChange('bloodPressure')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Oxygen Saturation (%)"
                                        value={vitals.oxygenSaturation}
                                        onChange={handleVitalChange('oxygenSaturation')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Blood Sugar (mg/dL)"
                                        value={vitals.bloodSugar}
                                        onChange={handleVitalChange('bloodSugar')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Heart Pulse (bpm)"
                                        type="number"
                                        value={vitals.heartPulse}
                                        onChange={handleVitalChange('heartPulse')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Respiratory Rate"
                                        value={vitals.respiratory}
                                        onChange={handleVitalChange('respiratory')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Height (cm)"
                                        type="number"
                                        value={vitals.height}
                                        onChange={handleVitalChange('height')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Weight (kg)"
                                        type="number"
                                        value={vitals.weight}
                                        onChange={handleVitalChange('weight')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="BMI"
                                        type="number"
                                        value={vitals.bmi}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>}

                        <div>
                            <h1 className="m-b-20 center-text">Lab Report</h1>
                            <div className="flex m-b-20" style={isMobile ? { flexDirection: 'column' } : {}}>
                                {!readOnly && !review && (
                                    <div className="flex-30" style={isMobile ? { width: '100%', marginBottom: '20px' } : {}}>
                                        {loading ? "Loading Templates..." : (
                                            <TemplateSelector
                                                templates={labServices}
                                                selectedTemplates={selectedTemplates}
                                                setSelectedTemplates={setSelectedTemplates}
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="flex-60" style={isMobile ? { width: '100%' } : {}}>
                                    {!readOnly && selectedTemplates.length > 0 && (
                                        <DynamicForm
                                            labTemplates={labServices}
                                            selectedTemplates={selectedTemplates}
                                            onSubmit={handleSave}
                                            defaultValues={initialFormValues}
                                            review={review}
                                        />
                                    )}

                                    {!readOnly && !review && (
                                        <div className="m-t-20 m-b-20">
                                            <Typography sx={{ color: "green" }} variant="h6">
                                                Please Upload laboratory results for AI assisted analysis
                                            </Typography>
                                            <UploadButton
                                                setdocumentArray={setDocumentArray}
                                                analyzeFile={analyzeFile}
                                            />
                                        </div>
                                    )}

                                    {/* Document display */}
                                    {documentArray?.map((doc, idx) => (
                                        <div key={idx} className="m-t-10 flex">
                                            <a href={doc.path} target="_blank" rel="noopener noreferrer" className="m-r-10">
                                                {doc.name}
                                            </a>
                                            <img style={{ width: "200px", height: "200px" }} src={doc.path} alt="" />
                                            {!review && (
                                                <RiDeleteBinLine
                                                    color="red"
                                                    className="pointer"
                                                    onClick={() => deleteDoc(doc.name)}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {readOnly && labResults?.patientLabDocuments?.map((doc, idx) => (
                                        <div key={idx} className="m-t-10 flex">
                                            <a href={doc.docPath} target="_blank" rel="noopener noreferrer" className="m-r-10">
                                                {doc.docName}
                                            </a>
                                            <img style={{ width: "200px", height: "200px" }} src={doc.docPath} alt="" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProcedureEditsModal;